import { useAgaveLink } from './useAgaveLink';
import {
  getAgaveAccountToken,
  getAgaveLinkToken,
  searchProjects as searchProjectsAction,
  persistAgaveAccountToken,
  selectAgaveSlice,
  getPersistedAgaveAccountToken,
} from 'app/pages/store/agaveSlice';

import { useAuth } from 'app/shared-components/auth/AuthProvider';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { captureException } from '@sentry/react';

export type AgaveIntegrationProps = { sourceSystem?: string | string[] };

export function useAgaveIntegration({ sourceSystem }: AgaveIntegrationProps = {}) {
  const dispatch = useAppDispatch();
  const authState = useAuth();
  const { user } = authState;
  const { linkToken, accountToken: agaveAccountToken, agaveLoading } = useAppSelector(selectAgaveSlice);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);

  useEffect(() => {
    // Check for persisted account token on mount
    if (selectedPaidol?.id && !agaveAccountToken) {
      dispatch(getPersistedAgaveAccountToken(selectedPaidol.id));
    }
  }, [dispatch, selectedPaidol?.id, agaveAccountToken]);

  const onSuccess = useCallback(
    (publicToken: string) => {
      dispatch(getAgaveAccountToken({ input: { public_token: publicToken } }))
        .unwrap()
        .then((agaveAccount) => {
          if (selectedPaidol?.id && agaveAccount?.account_token) {
            // Safely extract source system slug with fallback to undefined (optional field)
            const sourceSystemSlug = agaveAccount.source_system?.slug ?? undefined;

            dispatch(
              persistAgaveAccountToken({
                id: selectedPaidol.id,
                agaveAccountToken: agaveAccount.account_token,
                // Only set if we have a valid slug, otherwise omit (won't update field)
                ...(sourceSystemSlug && { agaveSourceSystemSlug: sourceSystemSlug }),
              })
            );
            dispatch(
              searchProjectsAction({
                input: {
                  filter: {
                    paidolId: selectedPaidol.id,
                  },
                  limit: 25,
                },
              })
            );
          }
        });
    },
    [dispatch, selectedPaidol?.id]
  );

  const { openLink, isReady } = useAgaveLink({
    referenceId: user!.sub,
    linkToken,
    showSandboxSourceSystems: process.env.REACT_APP_ENV === 'production' ? false : true, // Only for local development
    showProductionSourceSystems: true,
    sourceSystem: sourceSystem || ['sage-300-cre', 'vista-hosted', 'cmic', 'sage-100-contractor'],
    sourceSystemEnvironment: process.env.REACT_APP_ENV === 'production' ? 'prod' : 'sandbox',
    onSuccess,
  });

  // Loading should only be true when SDK isn't ready or we're actively fetching
  // Not when we simply don't have tokens (that's just unconnected state)
  const loading = useMemo(() => !isReady || agaveLoading, [isReady, agaveLoading]);
  const connected = useMemo(() => Boolean(agaveAccountToken), [agaveAccountToken]);

  // ref that tracks if we need to open the Agave modal (upon fetching the link token)
  const pendingOpenRef = useRef(false);
  // effect to finally open the Agave modal once the SDK becomes ready after a link token fetch.
  useEffect(() => {
    if (pendingOpenRef.current && linkToken && isReady) {
      pendingOpenRef.current = false;
      openLink();
    }
  }, [linkToken, isReady, openLink]);

  /*
   * `openLink` cannot be called until the Agave SDK reports it is `isReady`. When we
   * do not yet have a `linkToken`, we first dispatch `getAgaveLinkToken()` which
   * causes the SDK to re-initialise. That means the immediate `isReady` value in
   * the promise callback is almost certainly `false`, requiring the user to click
   * the button a second time. We fix this by recording that an open is pending
   * and then triggering `openLink` in an effect that watches `isReady`.
   */

  const openPopup = useCallback(() => {
    // Only fetch link token if we don't have one and aren't already connected
    if (!linkToken && !agaveAccountToken) {
      pendingOpenRef.current = true;
      dispatch(getAgaveLinkToken())
        .unwrap()
        .catch((err) => {
          pendingOpenRef.current = false; // abort pending state on failure
          captureException(err);
        });
    } else if (linkToken && isReady) {
      openLink();
    }
  }, [linkToken, agaveAccountToken, dispatch, openLink, isReady]);

  return { loading, connected, openPopup, paidol: selectedPaidol };
}
